// Knowledge Graph — concept-level mastery tracking with dependency awareness
// Multi-cert: graph is per-cert, keyed by certId
// Tracks: concept mastery, skill dependencies, cross-domain links

import { certPath, read } from './firebase-store.js';

// ── Per-cert graph structures ──
// { [certId]: { skills, concepts, skillEdges, conceptEdges } }
const _graphs = {};

/**
 * Initialize the knowledge graph from a cert's question bank.
 *
 * @param {string} certId
 * @param {Array} questionBank - enriched question array
 */
export function initGraph(certId, questionBank) {
  if (!questionBank || questionBank.length === 0) {
    _graphs[certId] = { skills: {}, concepts: {}, skillEdges: {}, conceptEdges: {} };
    return;
  }

  const skills = {};
  const concepts = {};
  const skillEdges = {};
  const conceptEdges = {};
  const skillToQuestions = {};

  for (const q of questionBank) {
    const qSkills = q.skills || [];
    const qConcepts = q.conceptTags || [];
    const domain = q.domain || 1;

    for (const s of qSkills) {
      if (!skills[s]) skills[s] = { count: 0, domains: new Set(), concepts: new Set() };
      skills[s].count++;
      skills[s].domains.add(domain);
      for (const c of qConcepts) skills[s].concepts.add(c);

      if (!skillToQuestions[s]) skillToQuestions[s] = [];
      skillToQuestions[s].push(q.id);
    }

    for (const c of qConcepts) {
      if (!concepts[c]) concepts[c] = { count: 0, skills: new Set() };
      concepts[c].count++;
      for (const s of qSkills) concepts[c].skills.add(s);
    }

    for (let i = 0; i < qSkills.length; i++) {
      for (let j = i + 1; j < qSkills.length; j++) {
        const key = [qSkills[i], qSkills[j]].sort().join('|');
        skillEdges[key] = (skillEdges[key] || 0) + 1;
      }
    }

    for (let i = 0; i < qConcepts.length; i++) {
      for (let j = i + 1; j < qConcepts.length; j++) {
        const key = [qConcepts[i], qConcepts[j]].sort().join('|');
        conceptEdges[key] = (conceptEdges[key] || 0) + 1;
      }
    }
  }

  // Convert Sets to Arrays for storage
  const skillNodes = {};
  for (const [name, data] of Object.entries(skills)) {
    skillNodes[name] = {
      count: data.count,
      domains: Array.from(data.domains),
      concepts: Array.from(data.concepts),
      questions: skillToQuestions[name] || []
    };
  }

  const conceptNodes = {};
  for (const [name, data] of Object.entries(concepts)) {
    conceptNodes[name] = {
      count: data.count,
      skills: Array.from(data.skills)
    };
  }

  _graphs[certId] = {
    skills: skillNodes,
    concepts: conceptNodes,
    skillEdges,
    conceptEdges
  };
}

/**
 * Get the raw graph data for a cert.
 * @param {string} certId
 */
export function getGraph(certId) {
  return _graphs[certId] || null;
}

/**
 * Check if graph is initialized for a cert.
 * @param {string} certId
 */
export function isGraphReady(certId) {
  return !!_graphs[certId];
}

/**
 * Get graph stats for a cert.
 * @param {string} certId
 */
export function getGraphStats(certId) {
  const graph = _graphs[certId];
  if (!graph) return null;
  return {
    skills: Object.keys(graph.skills).length,
    concepts: Object.keys(graph.concepts).length,
    skillEdges: Object.keys(graph.skillEdges).length,
    conceptEdges: Object.keys(graph.conceptEdges).length,
    crossDomainSkills: Object.values(graph.skills).filter(s => s.domains.length > 1).length
  };
}

// ── Skill Queries ──

/**
 * Get related skills for a given skill (cert-scoped).
 * @param {string} certId
 * @param {string} skill
 * @param {number} limit
 */
export function getRelatedSkills(certId, skill, limit = 5) {
  const graph = _graphs[certId];
  if (!graph) return [];

  const related = [];
  for (const [key, count] of Object.entries(graph.skillEdges)) {
    const [a, b] = key.split('|');
    if (a === skill) related.push({ skill: b, strength: count });
    else if (b === skill) related.push({ skill: a, strength: count });
  }

  return related.sort((a, b) => b.strength - a.strength).slice(0, limit);
}

/**
 * Get prerequisite skills (cert-scoped).
 * @param {string} certId
 * @param {string} skill
 */
export function getPrerequisites(certId, skill) {
  const graph = _graphs[certId];
  if (!graph || !graph.skills[skill]) return [];

  const skillData = graph.skills[skill];
  const skillDomain = Math.min(...skillData.domains);
  const related = getRelatedSkills(certId, skill, 10);

  return related
    .filter(r => {
      const rData = graph.skills[r.skill];
      if (!rData) return false;
      const rDomain = Math.min(...rData.domains);
      return rDomain <= skillDomain;
    })
    .map(r => r.skill);
}

/**
 * Get all skills for a concept (cert-scoped).
 * @param {string} certId
 * @param {string} concept
 */
export function getSkillsForConcept(certId, concept) {
  const graph = _graphs[certId];
  if (!graph || !graph.concepts || !graph.concepts[concept]) return [];
  return graph.concepts[concept].skills || [];
}

/**
 * Get all concepts for a skill (cert-scoped).
 * @param {string} certId
 * @param {string} skill
 */
export function getConceptsForSkill(certId, skill) {
  const graph = _graphs[certId];
  if (!graph || !graph.skills || !graph.skills[skill]) return [];
  return graph.skills[skill].concepts || [];
}

/**
 * Get cross-domain skill links (cert-scoped).
 * @param {string} certId
 */
export function getCrossDomainLinks(certId) {
  const graph = _graphs[certId];
  if (!graph) return [];

  return Object.entries(graph.skills)
    .filter(([_, data]) => data.domains.length > 1)
    .map(([name, data]) => ({
      skill: name,
      domains: data.domains,
      count: data.count
    }))
    .sort((a, b) => b.domains.length - a.domains.length || b.count - a.count);
}

// ── Concept Mastery (Firebase-backed, cert-scoped) ──

/**
 * Compute concept mastery scores from BKT skill mastery data (cert-scoped).
 * @param {string} certId
 * @param {string} userId
 */
export async function getConceptMastery(certId, userId) {
  const graph = _graphs[certId];
  if (!graph) return {};

  let masteryData = null;
  try {
    masteryData = await read(certPath(userId, certId, 'mastery'));
  } catch (e) { /* ignore */ }

  if (!masteryData) return {};

  const conceptMastery = {};

  for (const [conceptName, conceptData] of Object.entries(graph.concepts)) {
    const relatedSkills = conceptData.skills;
    if (!relatedSkills || relatedSkills.length === 0) continue;

    let totalWeight = 0;
    let weightedMastery = 0;
    const skillDetails = [];

    for (const skillName of relatedSkills) {
      const skillMastery = masteryData[skillName];
      const pL = skillMastery ? (skillMastery.pL || 0) : 0;
      const weight = graph.skills[skillName] ? graph.skills[skillName].count : 1;

      weightedMastery += pL * weight;
      totalWeight += weight;
      skillDetails.push({ name: skillName, pL });
    }

    conceptMastery[conceptName] = {
      mastery: totalWeight > 0 ? Math.round(weightedMastery / totalWeight * 100) / 100 : 0,
      skillCount: relatedSkills.length,
      skills: skillDetails
    };
  }

  return conceptMastery;
}

/**
 * Get weak concepts (cert-scoped).
 * @param {string} certId
 * @param {string} userId
 * @param {number} threshold
 */
export async function getWeakConcepts(certId, userId, threshold = 0.6) {
  const conceptMastery = await getConceptMastery(certId, userId);
  const graph = _graphs[certId];

  return Object.entries(conceptMastery)
    .filter(([_, data]) => data.mastery < threshold)
    .map(([name, data]) => ({
      concept: name,
      mastery: data.mastery,
      skillCount: data.skillCount,
      questionCount: graph && graph.concepts[name] ? graph.concepts[name].count : 0
    }))
    .sort((a, b) => a.mastery - b.mastery);
}

/**
 * Get questions related to a specific concept via its skills (cert-scoped).
 * @param {string} certId
 * @param {string} concept
 */
export function getQuestionsForConcept(certId, concept) {
  const graph = _graphs[certId];
  if (!graph || !graph.concepts[concept]) return [];

  const relatedSkills = graph.concepts[concept].skills;
  const questionIds = new Set();

  for (const skill of relatedSkills) {
    const skillData = graph.skills[skill];
    if (skillData && skillData.questions) {
      for (const qId of skillData.questions) {
        questionIds.add(qId);
      }
    }
  }

  return Array.from(questionIds);
}

/**
 * Compute skill coverage from BKT mastery data (cert-scoped).
 * @param {string} certId
 * @param {object} skillMastery - { [skillId]: { pL, ... } }
 * @param {number} masteryThreshold
 */
export function computeCoverage(certId, skillMastery, masteryThreshold = 0.6) {
  const graph = _graphs[certId];
  if (!graph) return { coverage: 0, coveredSkills: 0, totalSkills: 0, domains: {} };

  const totalSkills = Object.keys(graph.skills).length;
  if (totalSkills === 0) return { coverage: 0, coveredSkills: 0, totalSkills: 0, domains: {} };

  let coveredSkills = 0;
  const domainCoverage = {};

  for (const [skillName, skillData] of Object.entries(graph.skills)) {
    const mastery = skillMastery && skillMastery[skillName];
    const covered = mastery && mastery.pL >= masteryThreshold;
    if (covered) coveredSkills++;

    for (const d of skillData.domains) {
      if (!domainCoverage[d]) domainCoverage[d] = { total: 0, covered: 0 };
      domainCoverage[d].total++;
      if (covered) domainCoverage[d].covered++;
    }
  }

  const domains = {};
  for (const [d, data] of Object.entries(domainCoverage)) {
    domains[d] = {
      coverage: data.total > 0 ? Math.round(data.covered / data.total * 100) / 100 : 0,
      coveredSkills: data.covered,
      totalSkills: data.total
    };
  }

  return {
    coverage: Math.round(coveredSkills / totalSkills * 100) / 100,
    coveredSkills,
    totalSkills,
    domains
  };
}
